import { memo } from 'react'
import { Node, Edge } from 'reactflow'
import { Button } from "@/shared/components/ui/button"
import { Label } from "@/shared/components/ui/label"
import { Switch } from "@/shared/components/ui/switch"
import { X } from "lucide-react"

interface EdgeConfigPanelProps {
  selectedEdge: Edge | null
  onClose: () => void
  onUpdateEdgeData: (field: string, value: unknown) => void
  onSetPrimaryBranch: (edgeId: string, isPrimary: boolean) => void
  nodes: Node[]
  edges: Edge[]
}

export const EdgeConfigPanel = memo(({
  selectedEdge,
  onClose,
  onUpdateEdgeData,
  onSetPrimaryBranch,
  nodes,
  edges
}: EdgeConfigPanelProps) => {
  
  if (!selectedEdge) return null

  // 获取源节点和目标节点
  const sourceNode = nodes.find(n => n.id === selectedEdge.source)
  const targetNode = nodes.find(n => n.id === selectedEdge.target)
  
  // 只有从Binder节点出发的边才显示配置
  const isBinderEdge = sourceNode?.type === 'binder'
  
  if (!isBinderEdge) {
    return (
      <aside className="w-80 bg-white border-l border-slate-200 shadow-lg flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">连线配置</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <div className="text-center py-8">
            <span className="text-4xl">🔗</span>
            <p className="text-sm text-slate-600 mt-2">普通连线</p>
            <p className="text-xs text-slate-400 mt-1">
              {sourceNode?.data.label} → {targetNode?.data.label}
            </p>
            <p className="text-xs text-slate-400 mt-4">
              只有从并行编排器出发的连线才需要配置
            </p>
          </div>
        </div>
      </aside>
    )
  }

  // 获取所有从同一个Binder节点出发的边，用于主分支唯一性检查
  const siblingEdges = edges.filter(edge => 
    edge.source === selectedEdge.source && edge.id !== selectedEdge.id
  )
  
  const currentIsPrimary = selectedEdge.data?.is_primary ?? false
  const currentIsSilent = selectedEdge.data?.silent ?? false
  const hasSiblingPrimary = siblingEdges.some(edge => edge.data?.is_primary)

  return (
    <aside className="w-80 bg-white border-l border-slate-200 shadow-lg flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800">分支配置</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* 分支信息 */}
        <div className="p-3 bg-slate-50 rounded-lg">
          <h4 className="text-sm font-medium text-slate-700 mb-2">分支信息</h4>
          <div className="space-y-1 text-xs text-slate-600">
            <div>源节点: {sourceNode?.data.label}</div>
            <div>目标节点: {targetNode?.data.label}</div>
            <div>分支ID: {selectedEdge.id}</div>
          </div>
        </div>

        {/* 主分支设置 */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">主分支</Label>
              <p className="text-xs text-slate-500 mt-1">
                主分支的输出会保存到对话记忆中
              </p>
            </div>
            <Switch
              checked={currentIsPrimary}
              onCheckedChange={(checked) => {
                onSetPrimaryBranch(selectedEdge.id, checked)
              }}
            />
          </div>

          {/* 主分支冲突警告 */}
          {currentIsPrimary && hasSiblingPrimary && (
            <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
              ⚠️ 检测到多个主分支，建议只保留一个主分支
            </div>
          )}

          {!currentIsPrimary && !hasSiblingPrimary && (
            <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
              💡 建议设置一个主分支用于保存对话记忆
            </div>
          )}
        </div>

        {/* 静默模式设置 */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">静默模式</Label>
              <p className="text-xs text-slate-500 mt-1">
                静默分支不会向用户输出内容
              </p>
            </div>
            <Switch
              checked={currentIsSilent}
              onCheckedChange={(checked) => onUpdateEdgeData('silent', checked)}
            />
          </div>
        </div>

        {/* 配置说明 */}
        <div className="mt-6 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
          <h4 className="text-sm font-medium text-cyan-800 mb-2">💡 配置说明</h4>
          <ul className="text-xs text-cyan-700 space-y-1">
            <li>• 主分支：输出保存到对话记忆，影响后续对话</li>
            <li>• 静默分支：执行但不输出，适合后台任务</li>
            <li>• 建议只设置一个主分支避免记忆冲突</li>
            <li>• 所有分支都会并行执行</li>
          </ul>
        </div>
      </div>
    </aside>
  )
})

EdgeConfigPanel.displayName = 'EdgeConfigPanel'